import { endOfDay, format, parse, startOfDay, startOfMonth } from 'date-fns';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Pagination, PaginationParams } from '@bonusly/common-admin';
import {
	Box,
	Button,
	CheveronSmallDownOutline,
	CheveronSmallUpOutline,
	DocumentDownloadOutline,
	Flex,
	HStack,
	IconButton,
	Link,
	StyleProps,
	Table,
	Tbody,
	Td,
	Text,
	Tfoot,
	Th,
	Thead,
	Tr,
} from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { positiveInteger, useQueriesControl } from '../../../../lib/url/hooks/use-queries-control';
import { deserializeStringList, serializeStringList } from '../../../../lib/url/url-filter-params';
import { useProfilePath } from '../../../profile/hooks/use-profile-path';
import { GetIncentivesSummaryParams, useGetIncentivesSummary } from '../hooks/use-incentives';
import { formatDisplayDateWithYear } from '../utils/date';
import { AttachmentButton } from './attachment-button';
import { SkeletonTableRows } from './skeleton-table-rows';
import { TableFilters } from './table-filters';

const PAGE_SIZE = 24;
const URL_DATE_FORMAT = 'yyyy-MM-dd';
const today = new Date();
const monthStart = startOfMonth(today);

export const defaultParams: GetIncentivesSummaryParams = {
	award_ids: [],
	end_date: endOfDay(today).toISOString(),
	page_number: 1,
	page_size: PAGE_SIZE,
	sort_order: 'desc',
	start_date: monthStart.toISOString(),
};

export const IncentivesTable: FC = () => {
	const { t } = useTranslation('admin', {
		keyPrefix: 'incentives.summary',
	});
	const company = useCurrentCompany();
	const recognizeUrl = useBuildRecognizeURL();
	const { getProfileUrl } = useProfilePath();

	const { track } = useAnalytics();

	const [queries, setQueries] = useQueriesControl(['start_date', 'end_date', 'award_ids', 'sort_order', 'page_number'] as const, {
		defaultValue: {
			start_date: format(monthStart, URL_DATE_FORMAT),
			end_date: format(today, URL_DATE_FORMAT),
			award_ids: '',
			sort_order: 'desc',
			page_number: '1',
		},
		allowedValues: {
			sort_order: ['asc', 'desc'],
		},
	});

	// Derive the API-shaped params object from URL state. URL stores `yyyy-MM-dd` for
	// readability; the API expects ISO timestamps with start/end of day applied.
	const params: GetIncentivesSummaryParams = useMemo(
		() => ({
			award_ids: deserializeStringList(queries.award_ids),
			start_date: startOfDay(parse(queries.start_date, URL_DATE_FORMAT, new Date())).toISOString(),
			end_date: endOfDay(parse(queries.end_date, URL_DATE_FORMAT, new Date())).toISOString(),
			sort_order: queries.sort_order === 'asc' ? 'asc' : 'desc',
			page_number: positiveInteger(queries.page_number),
			page_size: PAGE_SIZE,
		}),
		[queries.award_ids, queries.start_date, queries.end_date, queries.sort_order, queries.page_number]
	);

	// Adapter that keeps the existing TableFilters/NameFilter prop contract intact.
	// Children call `setParams(nextParams)` with the full ISO-timestamped shape; this
	// translates back to URL keys (date-only, sort, page, awards) without touching
	// `page_size` (constant) or other API-internal fields.
	const setParams = useCallback(
		(next: GetIncentivesSummaryParams) => {
			setQueries({
				start_date: format(new Date(next.start_date), URL_DATE_FORMAT),
				end_date: format(new Date(next.end_date), URL_DATE_FORMAT),
				award_ids: serializeStringList(next.award_ids),
				sort_order: next.sort_order,
				page_number: next.page_number,
			});
		},
		[setQueries]
	);

	const { data, isFetching } = useGetIncentivesSummary(params);

	const awards = data?.awards || [];
	const incentives = data?.claims;
	const pagination = data?.pagination;
	const hasPagination = Boolean(data?.pagination?.page_count && data.pagination.page_count > 1);

	const handleSortByDate = () => {
		const newSortOrder = params.sort_order === 'asc' ? 'desc' : 'asc';
		setParams({
			...params,
			// NOTE: reset to page 1 on sort
			page_number: 1,
			sort_order: newSortOrder,
		});
	};

	const handleDownload = useCallback(() => {
		track(AnalyticsEvent.AdminIncentives.SummaryExportCsv);

		const startDate = params.start_date.replaceAll(':', '%3A');
		const endDate = params.end_date.replaceAll(':', '%3A');

		let url = `${recognizeUrl('api/v2', 'admin/incentives/summary/export_csv')}?start_date=${startDate}&end_date=${endDate}`;

		if (params.award_ids) {
			params.award_ids.forEach((award_id) => {
				url += `&award_ids%5B%5D=${award_id}`;
			});
		}

		window.open(url, '_blank');
	}, [params.award_ids, params.end_date, params.start_date, recognizeUrl, track]);

	const setPaginationData = (newPaginationData: PaginationParams) => {
		track(AnalyticsEvent.AdminIncentives.SummaryPaginate, {
			page: newPaginationData.page_number || 1,
		});

		setParams({
			...params,
			page_number: newPaginationData.page_number || 1,
		});
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	return (
		<Box backgroundColor="bg.surface" borderRadius="lg" overflow="hidden">
			<HStack justifyContent="space-between">
				<TableFilters awards={awards} params={params} setParams={setParams} />
				<Button mr={4} onClick={handleDownload} data-testid="export-button">
					<DocumentDownloadOutline size="sm" mr={2} />
					<Text fontSize={100} fontWeight="medium">
						{t('csv_export')}
					</Text>
				</Button>
			</HStack>
			<Table variant="simple" layout="fixed">
				<Thead>
					<Tr backgroundColor="bg.soft" width="100%" _last={{ borderBottom: 'none' }}>
						<TableHeader w="11%">
							<Flex alignItems="center">
								{t('header.date')}
								<IconButton
									aria-label={t('header.sort_by_date')}
									data-testid="sort-button"
									onClick={handleSortByDate}
									_hover={{ bg: 'transparent' }}
									_active={{ bg: 'transparent' }}
								>
									{params.sort_order === 'desc' ? (
										<CheveronSmallDownOutline color="text.softer" size="sm" />
									) : (
										<CheveronSmallUpOutline color="text.softer" size="sm" />
									)}
								</IconButton>
							</Flex>
						</TableHeader>
						<TableHeader w="16%">{t('header.name')}</TableHeader>
						<TableHeader w="7%">{t('header.amount')}</TableHeader>
						<TableHeader w="13%">{t('header.recipient')}</TableHeader>
						<TableHeader w="18%">{t('header.reason')}</TableHeader>
						<TableHeader w="12%">{t('header.attachment')}</TableHeader>
						<TableHeader w="10%">{t('header.status')}</TableHeader>
						<TableHeader w="12%">{t('header.decided_by')}</TableHeader>
					</Tr>
				</Thead>
				<Tbody>
					{isFetching ? (
						<SkeletonTableRows numberOfRows={24} />
					) : (
						incentives?.map((incentive, index) => {
							const displayDate = formatDisplayDateWithYear(incentive.decision_date);
							const amount = incentive.award.amount;
							const pointsName = amount === 1 ? company.budget.singular : company.budget.plural;
							const isLast = !hasPagination && index === incentives.length - 1;

							return (
								<Tr key={index}>
									<TableData data-testid="date-field" isLast={isLast}>
										{displayDate}
									</TableData>
									<TableData isLast={isLast} fontWeight="semibold">
										{incentive.award.name}
									</TableData>
									<TableData isLast={isLast}>
										{amount} {pointsName}
									</TableData>
									<TableData isLast={isLast}>
										{incentive.receiver[0] ? (
											<Link
												href={getProfileUrl(incentive.receiver[0].id)}
												target="_blank"
												color="primary.fg"
												fontWeight="medium"
												_hover={{
													color: 'primary.fgStrong',
													textDecoration: 'underline',
												}}
											>
												{incentive.receiver[0].display_name}
											</Link>
										) : (
											'-'
										)}
									</TableData>
									<TableData isLast={isLast}>{incentive.claim_reason || '-'}</TableData>
									<TableData isLast={isLast}>
										<AttachmentButton attachment={incentive.claim_attachment} />
									</TableData>
									<TableData isLast={isLast}>
										{t(`status.${incentive.state}`, {
											defaultValue: incentive.state,
										})}
									</TableData>
									<TableData isLast={isLast}>
										{incentive.decided_by ? (
											<Link
												href={getProfileUrl(incentive.decided_by.id)}
												target="_blank"
												color="primary.fg"
												fontWeight="medium"
												_hover={{
													color: 'primary.fgStrong',
													textDecoration: 'underline',
												}}
											>
												{incentive.decided_by.display_name}
											</Link>
										) : (
											'-'
										)}
									</TableData>
								</Tr>
							);
						})
					)}
					{!isFetching && !incentives?.length && (
						<Tr>
							<Td colSpan={8} textAlign="center" borderBottom="none">
								<Text color="text.softer">{t('no_data')}</Text>
							</Td>
						</Tr>
					)}
				</Tbody>
				{hasPagination && (
					<Tfoot>
						<Tr>
							<TableData colSpan={1} isLast>
								<Text color="text.softest" display="inline" fontSize={50} lineHeight={5} whiteSpace="nowrap">
									{t('pagination_count', {
										page_count: incentives?.length,
										total_count: pagination?.total_count,
									})}
								</Text>
							</TableData>
							<TableData colSpan={6} isLast>
								<Pagination
									data={{
										...pagination,
										enabled: true,
									}}
									setPaginationData={setPaginationData}
									fontSize={100}
								/>
							</TableData>
							<TableData colSpan={1} isLast />
						</Tr>
					</Tfoot>
				)}
			</Table>
		</Box>
	);
};

interface TableDataProps extends StyleProps {
	children?: React.ReactNode;
	colSpan?: number;
	isLast?: boolean;
}

export const TableData = ({ isLast, ...props }: TableDataProps) => {
	return <Td color="text.default" fontSize={100} fontWeight="normal" lineHeight={6} px={4} py={5} {...(isLast && { borderBottom: 'none' })} {...props} />;
};

const TableHeader = ({ ...props }) => {
	return <Th color="text.softer" fontSize={50} fontWeight="medium" lineHeight={5} px={4} py={2} textAlign="left" textTransform="capitalize" {...props} />;
};
